import { useCallback, useEffect, useMemo, useState } from 'react';
import { newClipboardItem, read, readText, write, writeText, } from './clipboard';
export function useNavigatorPermission(now, permissionName, onGranted, onError = reportError) {
    const [state, setState] = useState();
    useEffect(() => {
        if (!now)
            return;
        let canceled = false;
        let status;
        function onPermissionChange() {
            if (canceled)
                return;
            setState(status.state);
            if (status.state === 'granted') {
                onGranted?.();
            }
        }
        void navigator.permissions
            .query({ name: permissionName })
            .then((pStatus) => {
            if (canceled)
                return;
            status = pStatus;
            setState(status.state);
            status.addEventListener('change', onPermissionChange);
        })
            .catch((error) => {
            if (canceled)
                return;
            onError(error);
        });
        return () => {
            canceled = true;
            if (status) {
                status.removeEventListener('change', onPermissionChange);
            }
        };
    }, [now, permissionName, onGranted, onError]);
    return { state, isGranted: state === 'granted' };
}
/**
 * Give you a wrapped clipboard api and a shouldFallback state.
 * ignore if read and readText return promise filled with undefined, and rely on shouldFallback to display an alternative interaction.
 * You should use ClipboardFallback component
 *
 * @example
 * ```tsx
 *   const { readText, shouldFallback, setShouldFallback } = useClipboard();
 *
 *   function handlePasteAction() {
 *     // yes you can safely ignore error here,
 *     // readText() will never throw, error is caught and set shouldFallback to corresponding mode
 *     void readText().then(handlePaste);
 *   }
 *
 *   function handlePaste(text: string | undefined) {
 *     if (!text) return;
 *
 *     setState(text);
 *     setShouldFallback(null);
 *   }
 *
 *   return (
 *     <>
 *       <button onClick={handlePasteAction}>Paste</button>
 *       <ClipboardFallbackModal
 *         mode={shouldFallback}
 *         onDismiss={() => setShouldFallback(null)}
 *         onReadText={handlePaste}
 *       />
 *     </>
 *   )
 * ```
 */
export function useClipboard() {
    const [shouldFallback, setShouldFallback] = useState();
    const [text, setText] = useState();
    const [blobs, setBlobs] = useState();
    const cleanShouldFallback = useCallback(() => {
        setShouldFallback(undefined);
        setText(undefined);
        setBlobs(undefined);
    }, []);
    const clipboardAPI = useMemo(() => ({
        async read() {
            try {
                return await read();
            }
            catch {
                setShouldFallback('read');
            }
        },
        async readText() {
            try {
                return await readText();
            }
            catch {
                setShouldFallback('readText');
            }
        },
        async write(data) {
            try {
                return await write(data);
            }
            catch {
                const blobs = await Promise.all(data.map((ci) => ci.getType(ci.types[0])));
                setBlobs(blobs);
                setShouldFallback('write');
            }
        },
        async rawWriteWithType(data, type = 'text/plain') {
            try {
                const item = await newClipboardItem({
                    [type]: new Blob([data], { type }),
                });
                return await write([item]);
            }
            catch {
                setText(data);
                setShouldFallback('writeText');
            }
        },
        async writeText(data) {
            try {
                return await writeText(data);
            }
            catch {
                setText(data);
                setShouldFallback('writeText');
            }
        },
    }), []);
    return {
        ...clipboardAPI,
        shouldFallback,
        text,
        blobs,
        cleanShouldFallback,
    };
}
